import { createSelector,createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Roles, TenantGlobalRoles } from 'common/constants/roles';
import { fetchGlobalRoles } from 'common/services/api/GlobalRoles/GlobalRoles.thunks';
import { GlobalRole } from 'common/types/Entities';
import { differenceBy } from 'common/utils/ArrayDifferenceBy';

import type { RootState } from '../../app/store';

type GlobalRolesState = {
    globalRoles: GlobalRole[],
    loaded: boolean
};

const initialState: GlobalRolesState = {
    globalRoles: [],
    loaded: false
};

const globalRolesSlice = createSlice({
    name: 'globalRoles',
    initialState,
    reducers: () => ({}),

    extraReducers: builder => {
        builder.addCase(fetchGlobalRoles.pending, state => {
            state.loaded = false;
            state.globalRoles = [];
        });
        builder.addCase(fetchGlobalRoles.fulfilled, (state, action: PayloadAction<GlobalRole[]>) => {
            state.loaded = true;
            state.globalRoles = action.payload;
        });
        builder.addCase(fetchGlobalRoles.rejected, state => {
            state.loaded = false;
            state.globalRoles = [];
        });
    }
});

// eslint-disable-next-line no-restricted-syntax
export default globalRolesSlice.reducer;
export const selectGlobalRoles = (state: RootState) => state.globalRoles.globalRoles;

// Constants for role filtering
const TENANT_GLOBAL_ROLES_LIST = [
    { name: TenantGlobalRoles.Administrator },
    { name: TenantGlobalRoles.TenantACLManager },
    { name: TenantGlobalRoles.TenantRO },
];
const OEM_GLOBAL_ROLES_LIST = [
    { name: Roles.Admin },
    { name: Roles.AppManager },
    { name: Roles.GlobalRoleManager },
    { name: Roles.MultiTenantRO },
    { name: Roles.UserManager },
];

// Memoized selectors for OEM and non-OEM tenants
const selectGlobalRolesForOEMTenant = createSelector(
    [selectGlobalRoles],
    (globalRoles) => differenceBy<GlobalRole, { name: string }>(globalRoles, TENANT_GLOBAL_ROLES_LIST, 'name')
);

const selectGlobalRolesForNonOEMTenant = createSelector(
    [selectGlobalRoles],
    (globalRoles) => differenceBy<GlobalRole, { name: string }>(globalRoles, OEM_GLOBAL_ROLES_LIST, 'name')
);

// Parameterized selector that returns memoized selectors
export const selectGlobalRolesByTenantId = (isOEMTenant: boolean) =>
    isOEMTenant ? selectGlobalRolesForOEMTenant : selectGlobalRolesForNonOEMTenant;
